import React, { ComponentType, FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { Route } from 'react-router-dom';
import { ErrorBoundary } from '../ErrorBoundary/ErrorBoundary';
import { combineComponents } from '../../contexts/combineComponents';
import * as Sentry from '@sentry/react';
import { AuthContext } from '../../contexts/AuthContext';
import { CurrentUserContext } from '../../contexts/CurrentUserContext';

interface IProps {
  component: ComponentType;
  layout: ComponentType;
  providers?: FC[];
  requireUserInfo?: boolean;
  path: string;
}

export const SentryScope: FC<{ path: string }> = ({ path }) => {
  const { identity } = useContext(AuthContext);

  useEffect(() => {
    Sentry.configureScope((scope) => {
      if (identity) {
        scope.setUser({
          email: identity.email,
        });
      } else {
        scope.setUser(null);
      }
    });
  }, [identity?.email]);

  useEffect(() => {
    Sentry.configureScope((scope) => {
      scope.setTag('path', path);
    });
  }, [path]);

  return null;
};

export const RouteWrapper: FC<Omit<Route['props'], 'component'> & IProps> = ({
  component: Component,
  layout: Layout,
  providers = [],
  requireUserInfo = false,
  ...rest
}) => {
  const { userInfo } = useContext(CurrentUserContext);
  const prevProviders = useRef(providers);
  const memoizedProviders = useMemo(() => {
    if (
      providers.length !== prevProviders.current.length ||
      providers.some((provider, idx) => provider !== prevProviders.current[idx])
    ) {
      prevProviders.current = providers;
      return providers;
    }
    return prevProviders.current;
  }, [providers]);

  const CombinedProviders = useMemo(() => {
    return combineComponents(...memoizedProviders);
  }, [memoizedProviders]);

  const renderFn = useCallback(() => {
    return (
      <Layout>
        <ErrorBoundary key={rest.path}>
          {!requireUserInfo || !!userInfo ? (
            <CombinedProviders>
              <SentryScope path={rest.path} />
              <Component />
            </CombinedProviders>
          ) : null}
        </ErrorBoundary>
      </Layout>
    );
  }, [Component, Layout, CombinedProviders, !!userInfo]);

  return <Route render={renderFn} {...rest} />;
};
